// The family: people, their figures, linked phones and the parent PIN (docs/ORGANISER_PLAN.md §4).
import { z } from 'zod'

export const PersonKind = z.enum(['admin', 'adult', 'child'])
export type PersonKind = z.infer<typeof PersonKind>

export const AgeBand = z.enum(['u8', '8-12', '13+'])
export type AgeBand = z.infer<typeof AgeBand>

export const MAX_CHILDREN = 4
export const MAX_ADULTS = 1

const colour = z.number().int().min(0).max(0xffffff)

/** A toy figure; maps onto PersonColors in src/map/actors/person.ts. */
export const Figure = z.object({
  coat: colour,
  legs: colour.optional(),
  hat: colour.optional(),
  hatKind: z.enum(['beanie', 'cap', 'hood', 'earmuffs', 'none']).optional(),
  scarf: colour.optional(),
  prop: z.enum(['board', 'skis', 'sled', 'camera', 'clipboard', 'none']).optional(),
})
export type Figure = z.infer<typeof Figure>

export const PIN_LENGTH = 6
export const Pin = z.string().regex(/^\d{6}$/, '6 digits')

/** Invite codes: 6 characters from an alphabet without look-alikes (no 0/O, 1/I/L). */
export const INVITE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ'
export const InviteCode = z
  .string()
  .transform((s) => s.toUpperCase().replace(/[^0-9A-Z]/g, ''))
  .pipe(z.string().length(6))

export const LinkRequest = z.object({
  deviceId: z.string().min(8).max(64),
  code: InviteCode.optional(),
  label: z.string().trim().min(1).max(40).optional(),
})

/**
 * Recovery codes (WI-21): shown once when a family is started, the only way back in if every
 * phone is lost (no email, by design). 16 characters from the invite alphabet, about 78 bits,
 * shown as four groups of four; spaces and dashes are ignored when typed.
 */
export const RECOVERY_LENGTH = 16
export const RecoveryCode = z
  .string()
  .transform((s) => s.toUpperCase().replace(/[^0-9A-Z]/g, ''))
  .pipe(z.string().length(RECOVERY_LENGTH))

const FamilyName = z.string().trim().min(1).max(40)

/**
 * "Set it up" on this phone (WI-21, WI-37): a family and its parent (with a PIN), or a solo
 * traveller (a family of one whose grown-up plays), plus any children, and this phone.
 */
export const StartFamilyRequest = z
  .object({
    /** Just me: the traveller plays, and the family is named after them. */
    solo: z.boolean().optional(),
    family: FamilyName.optional(),
    name: z.string().trim().min(1).max(24),
    figure: Figure.optional(),
    pin: Pin,
    children: z
      .array(z.object({ name: z.string().trim().min(1).max(24), ageBand: AgeBand, figure: Figure.optional() }))
      .max(MAX_CHILDREN)
      .optional(),
    deviceId: z.string().min(8).max(64),
    label: z.string().trim().min(1).max(40).optional(),
  })
  .refine((r) => r.solo || r.family, { message: 'a family needs a name', path: ['family'] })
  .refine((r) => !r.solo || !r.children?.length, { message: 'just me has no children', path: ['children'] })
export type StartFamilyRequest = z.infer<typeof StartFamilyRequest>

/** What a solo traveller's family is called behind the scenes. */
export const soloFamilyName = (name: string) => `${name}'s trips`

/** Back in with the recovery code: links this phone and signs the parent in (to set a new PIN). */
export const RecoverRequest = z.object({
  code: RecoveryCode,
  deviceId: z.string().min(8).max(64),
  label: z.string().trim().min(1).max(40).optional(),
})

export const PinRequest = z.object({ pin: Pin })
export const SetPinRequest = z.object({ pin: Pin, oldPin: Pin.optional() })

export interface Invite {
  code: string
  expiresAt: number
}

export interface Device {
  id: string
  label: string
  linkedAt: number
  lastSeen: number
  /** Who last signed in on it. */
  people: string[]
  /** Whose phone it is (WI-17): a grown-up signed in on a child's phone is in parent mode. */
  resident: string | null
  current: boolean
}

/** "Make this <name>'s phone" (WI-17). */
export const ResidentRequest = z.object({ personId: z.string().min(1).max(64) })

/** Why the server refused a sign-in or a link (body of 401/403/429 responses). */
export type AuthError =
  /** setup: a local dev server links this phone to its one family without a code. */
  | { error: 'not linked'; setup: boolean }
  | { error: 'pin required' }
  | { error: 'wrong pin'; triesLeft: number }
  | { error: 'locked'; until: number }
  | { error: 'bad code' }
  /** WI-16: a grown-up with no PIN can't be picked on a child's phone, nor delete anything. */
  | { error: 'set a PIN first' }

const Name = z.string().trim().min(1).max(24)

/** Adding someone (admin). Children get an age band; a figure is optional (a default is used). */
export const NewPersonRequest = z.object({
  name: Name,
  kind: z.enum(['child', 'adult']),
  ageBand: AgeBand.optional(),
  figure: Figure.optional(),
})

export const UpdatePersonRequest = z
  .object({
    name: Name,
    ageBand: AgeBand.nullable(),
    figure: Figure,
    sort: z.number().int().min(0).max(99),
    /** A grown-up joins in (WI-37); children always play, so it's ignored for them. */
    plays: z.boolean(),
  })
  .partial()

/**
 * Running the family (B15): `co` makes a grown-up a second parent (co-admin), `transfer` hands the
 * parent role over (the caller becomes a grown-up), `revoke` turns a co-admin back into a grown-up.
 * A family always keeps at least one parent. Passkeys come later.
 */
export const AdminChangeRequest = z.object({ personId: z.string().min(1), mode: z.enum(['co', 'transfer', 'revoke']) })
export type AdminChangeRequest = z.infer<typeof AdminChangeRequest>

/** GET /api/family (WI-19): what the set-up checklist needs to know about the family itself. */
export interface FamilyInfo {
  name: string
  /** A recovery code exists (families started before codes existed have none until one is made). */
  recovery: boolean
}
